import * as math from "mathjs";
import { DataTypeSpecifier } from "../HistoricalData.js";
import { SensorType } from "../shared.js";
import { dayMs, hourMs } from "../timeConstants.js";
import { initializeUnits } from "../utils/addMathUnits.js";
import { SensorConfigurationsNoExplicitKeys } from "./SensorConfigurations/SensorConfiguration.js";

initializeUnits();

export type MeasurementType =
    | "soil moisture"
    | "temperature"
    | "humidity"
    | "pipe flow"
    | "scheduled irrigation"
    | "pipe pressure"
    | "pulse count"
    | "other";
export type DataTypeConfiguration = {
    dataType: DataTypeSpecifier;
    databaseUnit: string;
    defaultUserUnit?: string;
    measureType?: MeasurementType;
    alternateUnits?: string[];
    msTillIrrelevant?: number;
};

type SensorParsedDataTypesAndNames = Pick<
    (typeof SensorConfigurationsNoExplicitKeys)[SensorType],
    "parsedDataType" | "friendlyName"
>;
type SensorRelatedDataType = SensorParsedDataTypesAndNames["parsedDataType"];

export const DataTypeConfigurations: Partial<Record<DataTypeSpecifier, DataTypeConfiguration>> &
    Record<SensorRelatedDataType, DataTypeConfiguration> = {
    moisture_watermark: {
        dataType: "moisture_watermark",
        databaseUnit: "kPa",
        defaultUserUnit: "kPa",
        measureType: "soil moisture",
        msTillIrrelevant: hourMs * 12,
    },
    v1flowmeter: {
        dataType: "v1flowmeter",
        databaseUnit: "LPM",
        defaultUserUnit: "LPM",
        measureType: "pipe flow",
        alternateUnits: ["GPM", "GPH", "LPH"],
        msTillIrrelevant: hourMs * 12,
    },
    moisture_vol: {
        dataType: "moisture_vol",
        databaseUnit: "percent",
        defaultUserUnit: "percent",
        measureType: "soil moisture",
        msTillIrrelevant: hourMs * 12,
    },
    v1transducer: {
        dataType: "v1transducer",
        databaseUnit: "kPa",
        defaultUserUnit: "psi",
        measureType: "pipe pressure",
        alternateUnits: ["psi", "bar"],
        msTillIrrelevant: dayMs * 2,
    },
    moisture_irrometer: {
        dataType: "moisture_irrometer",
        databaseUnit: "kPa",
        defaultUserUnit: "kPa",
        measureType: "soil moisture",
        msTillIrrelevant: hourMs * 12,
    },
    //this is sensoterra
    moisture: {
        dataType: "moisture",
        databaseUnit: "percent",
        defaultUserUnit: "percent",
        measureType: "soil moisture",
        msTillIrrelevant: dayMs,
    },
    temperature: {
        dataType: "temperature",
        databaseUnit: "degC",
        defaultUserUnit: "degC",
        alternateUnits: ["degF"],
        measureType: "temperature",
        msTillIrrelevant: dayMs * 2,
    },
    humidity: {
        dataType: "humidity",
        databaseUnit: "percentRH",
        defaultUserUnit: "percentRH",
        measureType: "humidity",
        msTillIrrelevant: dayMs * 2,
    },
    valveOpenPercent: {
        dataType: "valveOpenPercent",
        databaseUnit: "percent",
        defaultUserUnit: "percent",
        measureType: "other",
        msTillIrrelevant: dayMs * 7,
    },
    isOnline: {
        dataType: "isOnline",
        databaseUnit: "percent",
        defaultUserUnit: "percent",
        measureType: "other",
        msTillIrrelevant: dayMs * 7,
    },
    valveOnlinePercent: {
        dataType: "valveOnlinePercent",
        databaseUnit: "percent",
        defaultUserUnit: "percent",
        measureType: "other",
        msTillIrrelevant: dayMs * 7,
    },
    voltage: {
        dataType: "voltage",
        databaseUnit: "V",
        defaultUserUnit: "V",
        measureType: "other",
    },
    waterTankLevel: {
        dataType: "waterTankLevel",
        databaseUnit: "m",
        defaultUserUnit: "m",
        measureType: "other",
    },
    draginoEC: {
        dataType: "draginoEC",
        databaseUnit: "μS/cm",
        defaultUserUnit: "μS/cm",
        measureType: "other",
    },
    stemWaterPotential: {
        dataType: "stemWaterPotential",
        databaseUnit: "kPa",
        defaultUserUnit: "kPa",
        measureType: "pipe pressure",
    },
    soilTemperature: {
        dataType: "soilTemperature",
        databaseUnit: "degC",
        defaultUserUnit: "degC",
        alternateUnits: ["degF"],
        measureType: "temperature",
    },
    moisture_drillAndDrop: {
        dataType: "moisture_drillAndDrop",
        databaseUnit: "percent",
        defaultUserUnit: "percent",
        measureType: "soil moisture",
    },
    salinity_drillAndDrop: {
        dataType: "salinity_drillAndDrop",
        databaseUnit: "percent",
        defaultUserUnit: "percent",
        measureType: "other",
    },
    gen4FlowPulseTotal: {
        dataType: "gen4FlowPulseTotal",
        databaseUnit: "pulse",
        defaultUserUnit: "pulse",
        measureType: "pulse count",
    },
};

export type RenderDataOptions = {
    preferredUnit?: string;
    originalUnit?: string;
    dataType?: DataTypeSpecifier;
    sigFigs: number;
    lowestPermissibleValue: number;
    includeUnit: boolean;
    includeNumber: boolean;
};

// TODO: Can we deprecate this function, now that we have the new unit configuration system?
// ref https://linear.app/verdiag/issue/SWD-2449/clean-up-old-datatypeconfiguration-based-unit-system
export function renderUnit(unit) {
    return unit.replace("deg", "°").replace("percent", "%");
}
/**
 * Renders data based on that data type and your preferred unit
 * @param dbValue
 * @param dataType
 * @param preferredUnit
 * @param options
 * @return {string}
 */
export function renderData(dbValue, options?: Partial<RenderDataOptions>): string {
    options = Object.assign(
        {
            sigFigs: 3,
            lowestPermissibleValue: 0.000001,
            includeUnit: true,
            includeNumber: true,
        },
        options,
    );
    const dataType = options.dataType;
    const preferredUnit = options.preferredUnit;
    const dataTypeConfig = DataTypeConfigurations[dataType];
    const originalUnit = options.originalUnit ?? dataTypeConfig?.databaseUnit;
    const outputUnit = preferredUnit ?? dataTypeConfig?.defaultUserUnit ?? dataTypeConfig?.databaseUnit ?? originalUnit;

    //if we don't have a unit, we can't do any conversion.
    if (!originalUnit) {
        if (options.includeNumber) {
            return (Math.round(dbValue * 100) / 100).toString();
        } else {
            return "";
        }
    }
    let result = "";
    if (options.includeNumber) {
        const unitedNumber = math.unit(dbValue, originalUnit);
        const convertedInput = unitedNumber.toNumber(outputUnit);
        const orderOfMagnitude = Math.max(
            Math.pow(10, Math.floor(Math.log(Math.abs(convertedInput)) / Math.LN10 + 0.000000001)),
            options.lowestPermissibleValue,
        );
        const factor = (1 / orderOfMagnitude) * Math.pow(10, options.sigFigs);
        result += (Math.round(convertedInput * factor) / factor).toString();
        if (options.includeUnit) {
            result += " ";
        }
    }

    if (options.includeUnit) {
        result += renderUnit(outputUnit);
    }

    return result;
}
